import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Lock, Users, Building2, Home, CalendarClock, ArrowLeft, RefreshCw,
  Search, X, MessageSquareText, Save, Phone, Mail,
} from "lucide-react";
import { API } from "../config";

const PATHWAY_LABEL = { business: "Business Funding", real_estate: "Real Estate Funding", advisor: "Advisor", general: "General" };
const PATHWAY_STYLE = {
  business: "bg-[#0B0B0D] text-white",
  real_estate: "bg-[#C0392B] text-white",
  advisor: "bg-emerald-700 text-white",
  general: "bg-slate-500 text-white",
};

const STATUSES = ["new", "contacted", "qualified", "closed"];
const STATUS_STYLE = {
  new: "bg-amber-100 text-amber-800 border-amber-200",
  contacted: "bg-blue-100 text-blue-800 border-blue-200",
  qualified: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
    ", " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-center justify-between">
      <p className="text-[12px] font-semibold uppercase tracking-[0.15em] text-[#64748B]">{label}</p>
      <Icon size={18} className="text-[#8E1B22]" strokeWidth={1.8} />
    </div>
    <p className="mt-3 font-display text-3xl font-bold text-[#0B0B0D]">{value}</p>
  </div>
);

function LeadDrawer({ lead, onClose, passcode, onUpdated }) {
  const [status, setStatus] = useState(lead.status || "new");
  const [notes, setNotes] = useState(lead.advisor_notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setStatus(lead.status || "new"); setNotes(lead.advisor_notes || ""); }, [lead]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/leads/${lead.id}`,
        { status, advisor_notes: notes },
        { headers: { "X-Admin-Passcode": passcode } });
      onUpdated(data);
    } catch (e) { console.error("Failed to update lead", e); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col overflow-hidden bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-[#0B0B0D] px-5 py-4">
          <div>
            <p className="font-display text-lg font-bold text-white">{lead.name || "Prospect"}</p>
            <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${PATHWAY_STYLE[lead.pathway]}`}>
              {PATHWAY_LABEL[lead.pathway] || lead.pathway}
            </span>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="rounded-full p-2 text-[#94A3B8] hover:bg-white/10 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="custom-scroll flex-1 space-y-6 overflow-y-auto px-5 py-5">
          {/* Contact */}
          <div className="space-y-1.5 text-[14px]">
            <div className="flex items-center gap-2 text-[#1E293B]"><Mail size={15} className="text-[#8E1B22]" /> {lead.email || "—"}</div>
            <div className="flex items-center gap-2 text-[#1E293B]"><Phone size={15} className="text-[#8E1B22]" /> {lead.phone || "—"}</div>
            <div className="flex items-center gap-2 text-[#64748B] text-[13px]"><CalendarClock size={15} /> Created {fmt(lead.created_at)}</div>
          </div>

          {/* Answers */}
          {lead.answers && Object.keys(lead.answers).length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8E1B22]">Pre-Qualification</p>
              <div className="rounded-xl border border-slate-200 bg-[#F8F9FA] p-3 text-[13px]">
                {Object.entries(lead.answers).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 py-1.5 last:border-0">
                    <span className="text-[#64748B]">{k.replace(/_/g, " ")}</span>
                    <span className="font-medium text-[#0B0B0D]">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transcript */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8E1B22]">
              <MessageSquareText size={13} /> Conversation
            </p>
            <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="transcript">
              {(!lead.transcript || lead.transcript.length === 0) && (
                <p className="text-[13px] text-[#94A3B8]">No conversation recorded.</p>
              )}
              {(lead.transcript || []).map((m, i) => (
                <div key={i} className={`flex ${m.role === "aria" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed ${
                    m.role === "aria" ? "bg-[#F1F3F7] text-[#1E293B] rounded-tl-md" : "bg-[#0B0B0D] text-white rounded-tr-md"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8E1B22]">Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button key={s} data-testid={`status-${s}`} onClick={() => setStatus(s)}
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold capitalize transition-all ${
                    status === s ? STATUS_STYLE[s] + " ring-2 ring-[#C0392B]/40" : "border-slate-200 bg-white text-[#64748B] hover:border-[#C0392B]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8E1B22]">Advisor Notes</p>
            <textarea data-testid="advisor-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
              placeholder="Add internal notes about this prospect…"
              className="w-full rounded-xl border border-slate-200 bg-[#F8F9FA] p-3 text-[14px] outline-none focus:ring-2 focus:ring-[#C0392B]" />
          </div>
        </div>

        <div className="border-t border-slate-200 p-4">
          <button data-testid="save-lead" onClick={save} disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#C0392B] px-4 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.01] disabled:opacity-60">
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [passcode, setPasscode] = useState(sessionStorage.getItem("aria_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, business: 0, real_estate: 0, advisor: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (code) => {
    setLoading(true); setError("");
    try {
      const headers = { "X-Admin-Passcode": code };
      const [l, s] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/leads/stats`, { headers }),
      ]);
      setLeads(l.data); setStats(s.data); setAuthed(true);
      sessionStorage.setItem("aria_admin", code);
    } catch (e) {
      setAuthed(false); setError("Invalid passcode. Please try again.");
      sessionStorage.removeItem("aria_admin");
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (passcode) load(passcode); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter !== "all" && l.pathway !== filter) return false;
      if (!q) return true;
      return [l.name, l.email, l.phone].filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });
  }, [leads, search, filter]);

  const onUpdated = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setSelected(updated);
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0B0D] px-6">
        <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#17171A] p-8">
          <img src="/afari-logo.png" alt="AFARI" className="mb-4 h-20 w-auto object-contain" />
          <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl border border-[#C0392B]/30 bg-[#0B0B0D]">
            <Lock size={20} className="text-[#C0392B]" strokeWidth={1.8} />
          </div>
          <h1 className="font-display text-2xl font-bold text-white">Advisor Portal</h1>
          <p className="mt-2 text-[13px] text-[#94A3B8]">Enter your passcode to view captured prospects.</p>
          <input data-testid="admin-passcode" type="password" value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(passcode)}
            placeholder="Passcode"
            className="mt-5 w-full rounded-lg border border-white/10 bg-[#0B0B0D] px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-[#C0392B]" />
          {error && <p data-testid="admin-error" className="mt-2 text-[13px] text-red-400">{error}</p>}
          <button data-testid="admin-login" onClick={() => load(passcode)} disabled={loading}
            className="mt-4 w-full rounded-full bg-[#C0392B] px-4 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
            {loading ? "Verifying…" : "Enter Portal"}
          </button>
          <Link to="/" className="mt-4 flex items-center justify-center gap-1.5 text-[13px] text-[#94A3B8] hover:text-white">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-10">
          <div className="flex items-center gap-3">
            <Link to="/"><img src="/afari-logo.png" alt="AFARI" className="h-12 w-auto object-contain" style={{ filter: "invert(0)" }} /></Link>
            <span className="rounded-full bg-[#F1F3F7] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#64748B]">Advisor Portal</span>
          </div>
          <button data-testid="admin-refresh" onClick={() => load(passcode)}
            className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-[13px] font-semibold text-[#0B0B0D] transition-colors hover:border-[#C0392B]">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 lg:px-10">
        <h1 className="font-display text-3xl font-bold text-[#0B0B0D]">Captured Prospects</h1>
        <p className="mt-1 text-[14px] text-[#64748B]">Leads collected by ARIA across all funding pathways.</p>

        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard icon={Users} label="Total Leads" value={stats.total} />
          <StatCard icon={Building2} label="Business" value={stats.business} />
          <StatCard icon={Home} label="Real Estate" value={stats.real_estate} />
          <StatCard icon={CalendarClock} label="Advisor" value={stats.advisor} />
        </div>

        {/* Controls */}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, phone…"
              className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-[14px] outline-none focus:ring-2 focus:ring-[#C0392B]" />
          </div>
          <div className="flex flex-wrap gap-2">
            {[["all", "All"], ["business", "Business"], ["real_estate", "Real Estate"], ["advisor", "Advisor"], ["general", "General"]].map(([k, label]) => (
              <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
                className={`rounded-full border px-3.5 py-2 text-[12px] font-semibold transition-all ${
                  filter === k ? "border-[#C0392B] bg-[#C0392B] text-white" : "border-slate-200 bg-white text-[#64748B] hover:border-[#C0392B]"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]" data-testid="leads-table">
              <thead className="border-b border-slate-200 bg-[#F8F9FA] text-[11px] uppercase tracking-[0.12em] text-[#64748B]">
                <tr>
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Contact</th>
                  <th className="px-5 py-3 font-semibold">Type</th>
                  <th className="px-5 py-3 font-semibold">Created</th>
                  <th className="px-5 py-3 font-semibold">Last Activity</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-[#94A3B8]">No matching leads.</td></tr>
                )}
                {filtered.map((l) => (
                  <tr key={l.id} onClick={() => setSelected(l)}
                    className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-[#F8F9FA]" data-testid={`lead-row-${l.id}`}>
                    <td className="px-5 py-4 font-medium text-[#0B0B0D]">{l.name || "—"}</td>
                    <td className="px-5 py-4 text-[#64748B]">
                      <div>{l.email || "—"}</div>
                      {l.phone && <div className="text-[12px]">{l.phone}</div>}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${PATHWAY_STYLE[l.pathway] || "bg-slate-500 text-white"}`}>
                        {PATHWAY_LABEL[l.pathway] || l.pathway}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-[#94A3B8]">{fmt(l.created_at)}</td>
                    <td className="px-5 py-4 text-[#94A3B8]">{fmt(l.updated_at || l.created_at)}</td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}>
                        {l.status || "new"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {selected && <LeadDrawer lead={selected} passcode={passcode} onClose={() => setSelected(null)} onUpdated={onUpdated} />}
    </div>
  );
}
